import apiClient from '@/api/client';
import type {
    StorageConfiguration,
    StorageConfigurationCreate,
    StorageConfigurationUpdate,
    StorageFolderInitResult,
    StorageTestResult,
} from '@/types/storage';

export class StorageConfigurationService {
    /**
     * List all storage configurations for the current user
     */
    async getConfigurations(): Promise<StorageConfiguration[]> {
        // Backend returns { configurations: [...], total, default_id }
        // but older clients may expect an array. Normalize to array here.
        const resp = await apiClient.get<
            { configurations: StorageConfiguration[]; total?: number; default_id?: number } | StorageConfiguration[]
        >('/api/storage/configurations');

        if (Array.isArray(resp)) {
            return resp;
        }
        if (resp && Array.isArray((resp as any).configurations)) {
            return (resp as any).configurations as StorageConfiguration[];
        }
        return [];
    }

    /**
     * Get a specific storage configuration by ID
     */
    async getConfiguration(id: number): Promise<StorageConfiguration> {
        return await apiClient.get<StorageConfiguration>(`/api/storage/configurations/${id}`);
    }

    /**
     * Create a new storage configuration
     */
    async createConfiguration(data: StorageConfigurationCreate): Promise<StorageConfiguration> {
        return await apiClient.post<StorageConfiguration>('/api/storage/configurations', data);
    }

    /**
     * Update an existing storage configuration
     */
    async updateConfiguration(id: number, data: StorageConfigurationUpdate): Promise<StorageConfiguration> {
        return await apiClient.put<StorageConfiguration>(`/api/storage/configurations/${id}`, data);
    }

    /**
     * Delete a storage configuration
     */
    async deleteConfiguration(id: number): Promise<void> {
        return await apiClient.delete<void>(`/api/storage/configurations/${id}`);
    }

    /**
     * Test connectivity and permissions for a storage configuration
     */
    async testConfiguration(id: number): Promise<StorageTestResult> {
        return await apiClient.post<StorageTestResult>(`/api/storage/configurations/${id}/test`);
    }

    /**
     * Set a storage configuration as default for the user
     */
    async setDefaultConfiguration(id: number): Promise<StorageConfiguration> {
        return await apiClient.post<StorageConfiguration>(`/api/storage/configurations/${id}/set-default`);
    }

    /**
     * Initialize folder structure for a storage configuration
     */
    async initializeFolders(id: number): Promise<StorageFolderInitResult> {
        return await apiClient.post<StorageFolderInitResult>(`/api/storage/configurations/${id}/initialize-folders`, {});
    }

    async scanAndReconcile(
        id: number,
        dryRun = false,
        backfill = true,
        includePublished = true
    ): Promise<{
        result: any;
    }> {
        return await apiClient.post<{ result: any }>(`/api/storage/configurations/${id}/scan`, {
            dry_run: dryRun,
            backfill_assignments: backfill,
            include_published: includePublished,
        });
    }

    /**
     * Get the default storage configuration for the user
     */
    async getDefaultConfiguration(): Promise<StorageConfiguration | null> {
        const configurations = await this.getConfigurations();
        return configurations.find((config) => config.is_default) || null;
    }

    /**
     * Validate bucket name format
     */
    validateBucketName(bucketName: string): boolean {
        // Bucket name must be 3-63 characters, start and end with lowercase letter or number
        // Can contain lowercase letters, numbers, and hyphens
        const bucketRegex = /^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]$/;
        return bucketRegex.test(bucketName);
    }

    /**
     * Mask sensitive credentials for display
     */
    maskCredential(credential: string): string {
        if (!credential || credential.length < 8) {
            return '****';
        }
        return `${credential.slice(0, 3)}****${credential.slice(-3)}`;
    }

    /**
     * Format endpoint URL
     */
    formatEndpoint(endpoint: string, useSSL: boolean): string {
        // Remove any existing protocol
        const cleanEndpoint = endpoint.replace(/^https?:\/\//, '');
        // Add appropriate protocol
        return `${useSSL ? 'https' : 'http'}://${cleanEndpoint}`;
    }
}

export const storageConfigurationService = new StorageConfigurationService();
